import { BarChart3 } from 'lucide-react';
import { useMemo } from 'react';
import { Chart, getCommonBarChartOptionsStatic } from 'src/common/components/Chart';

import { Card, CardContent, CardHeader, CardTitle, Skeleton, Text } from '@/design-system/components/ui';
import { CHART_COLORS } from '@/design-system/tokens';

import { type GlobalBreakdownResult } from '../domain/types';

interface BreakdownChartProps {
  results: GlobalBreakdownResult[];
  isLoading: boolean;
}

export function BreakdownChart({ results, isLoading }: BreakdownChartProps) {
  const series = useMemo(
    () => [
      {
        name: 'Total Referral Volume',
        data: results.map((r) => r.totalReferralVolume),
      },
    ],
    [results],
  );

  const options = useMemo(() => {
    const base = getCommonBarChartOptionsStatic();
    return {
      ...base,
      colors: [CHART_COLORS.primary.main],
      xaxis: {
        ...base.xaxis,
        type: 'category' as const,
        // The API can return a null `groupKey` (affiliates with no region/status/
        // tier). ApexCharts sizes the x-axis by reducing `category.length` over
        // these labels, so a null here throws "Cannot read properties of null
        // (reading 'length')" and takes down the whole view. Coerce to a label.
        categories: results.map((r) => r.groupKey || 'Unknown'),
        labels: {
          style: { colors: 'var(--muted-foreground)' },
        },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => (value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value.toFixed(0)}`),
          style: { colors: 'var(--muted-foreground)' },
        },
      },
      tooltip: {
        y: {
          formatter: (value: number) => `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        },
      },
    };
  }, [results]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="tw:p-6">
          <Skeleton width="100%" height="300px" />
        </CardContent>
      </Card>
    );
  }

  if (results.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Referral Volume by Group</CardTitle>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:min-h-[300px]">
          <BarChart3 size={24} className="tw:text-muted-foreground" aria-hidden="true" />
          <Text variant="body-md" color="muted">
            No data available
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral Volume by Group</CardTitle>
      </CardHeader>
      <CardContent className="tw:p-6">
        <Chart type="bar" series={series} options={options} height={300} />
      </CardContent>
    </Card>
  );
}
